import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message } from '@/types';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { AgentSelector } from './AgentSelector';

interface ChatInterfaceProps {
  messages: Message[];
  onSendMessage: (content: string) => Promise<void>;
  onStartNewSession?: (agentId?: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
}

export function ChatInterface({
  messages,
  onSendMessage,
  onStartNewSession,
  isLoading,
  error,
  onClearError,
}: ChatInterfaceProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [input]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    setInput('');
    await onSendMessage(trimmed);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] glass rounded-2xl overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <WelcomeMessage onStartNewSession={onStartNewSession} />
        ) : (
          <AnimatePresence initial={false}>
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
          </AnimatePresence>
        )}

        {isLoading && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="mx-4 mb-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button onClick={onClearError} className="text-red-400 hover:text-red-300">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-4 border-t border-charcoal-700/50">
        <div className="flex gap-3 items-end">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask your coach about Faith, Family, Finance, or Fitness..."
              rows={1}
              className="w-full px-4 py-3 rounded-xl bg-charcoal-800 border border-charcoal-700
                       text-white placeholder-slate-500 resize-none
                       focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                       transition-all duration-200"
            />
          </div>
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="px-6 py-3 rounded-xl bg-royal-gradient text-white font-medium
                     hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-amber-500
                     disabled:opacity-50 disabled:cursor-not-allowed
                     transition-all duration-200 shadow-glow
                     flex items-center gap-2"
          >
            <span className="hidden sm:inline">Send</span>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-500 text-center">
          Press Enter to send, Shift+Enter for new line
        </p>
      </form>
    </div>
  );
}

interface WelcomeMessageProps {
  onStartNewSession?: (agentId?: string) => Promise<void>;
}

function WelcomeMessage({ onStartNewSession }: WelcomeMessageProps) {
  const [selectedAgentId, setSelectedAgentId] = useState<string>();

  const handleAgentSelect = async (agentId: string) => {
    setSelectedAgentId(agentId);
    if (onStartNewSession) {
      await onStartNewSession(agentId);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8">
      <div className="w-20 h-20 mb-6 rounded-2xl bg-royal-gradient flex items-center justify-center shadow-glow-lg">
        <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white mb-2 font-heading">
        Welcome to LevelUp Coach
      </h2>
      <p className="text-slate-400 mb-8 max-w-3xl">
        Choose a specialized coach to begin your journey toward balance and excellence.
      </p>
      <div className="w-full max-w-4xl">
        <AgentSelector onSelectAgent={handleAgentSelect} selectedAgentId={selectedAgentId} />
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  const { isSuperAdmin } = useAdminAuth();
  const metadata = message.metadata as Record<string, unknown> | undefined;
  const isQuotaError = metadata?.errorType === 'quota_exceeded';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[85%] px-4 py-3 rounded-2xl ${
          isUser
            ? 'bg-royal-gradient text-white rounded-br-md'
            : 'bg-charcoal-800 text-slate-200 rounded-bl-md border border-charcoal-700/50'
        }`}
      >
        {isQuotaError && isSuperAdmin && (
          <div className="mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs">
            <strong>Billing Alert:</strong> The LLM provider returned a quota or billing error.
            Check your plan and usage limits in the provider's dashboard.
          </div>
        )}
        <div className="prose-chat max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
        </div>
        <p
          className={`text-xs mt-2 ${
            isUser ? 'text-amber-200' : 'text-slate-500'
          }`}
        >
          {new Date(message.createdAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
    </motion.div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-charcoal-800 px-4 py-3 rounded-2xl rounded-bl-md border border-charcoal-700/50">
        <div className="flex gap-1">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-bounce" />
          <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce animation-delay-150" />
          <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce animation-delay-300" />
        </div>
      </div>
    </div>
  );
}
